import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChatMessage,
  ChatRoom,
  enterChatRoom,
  fetchChatMessages,
  fetchChatRoom,
  fetchCurrentMemberProfile,
  getCurrentUser,
  leaveChatRoom,
  navigate,
  OneliveSessionUser,
  resolveChatRoomIdFromLocation,
  sendChatMessage,
  sendChatImage,
  showOneliveAlert,
  showOneliveToast,
} from './api';
import { resolveChatRoomThumbOptional, roomCapacity } from './chatListShared';
import { handleLoginRequired } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';
import OnelivePageLayout from './OnelivePageLayout';

interface MobileChatRoomPageProps {
  roomId?: string | number;
}

type PagePhase = 'loading' | 'ready' | 'error';

function ChatCameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 4h6l1.2 2H19a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2.8L9 4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function formatMessageTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function resolveMemberAvatarUrl(profile: OneliveSessionUser | null): string | null {
  const url = profile?.avatar || profile?.member_profile?.avatar_url;
  if (!url || !String(url).trim()) return null;
  return String(url).trim();
}

function ChatDefaultAvatar({ className }: { className?: string }) {
  return (
    <span className={`onelive-chat-default-avatar${className ? ` ${className}` : ''}`} aria-hidden="true">
      <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="20" fill="#dce4ef" />
        <circle cx="20" cy="15" r="6" fill="#8fa3b8" />
        <path d="M8 32c2.2-5.2 6.8-8 12-8s9.8 2.8 12 8" fill="#8fa3b8" />
      </svg>
    </span>
  );
}

function ChatAvatar({ src, className }: { src?: string | null; className?: string }) {
  const [failed, setFailed] = useState(false);
  const hasAvatar = Boolean(src && src.trim() && !failed);

  if (!hasAvatar) {
    return <ChatDefaultAvatar className={className} />;
  }

  return (
    <img
      className={className}
      src={src!}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

export default function MobileChatRoomPage({ roomId }: MobileChatRoomPageProps) {
  const resolvedRoomId = useMemo(() => resolveChatRoomIdFromLocation(roomId), [roomId]);
  const [room, setRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [phase, setPhase] = useState<PagePhase>('loading');
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [entered, setEntered] = useState(false);
  const [myProfile, setMyProfile] = useState<OneliveSessionUser | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const enteredRef = useRef(false);
  const lastGiftIdRef = useRef(0);
  const lastScrolledMsgIdRef = useRef(0);
  const user = getCurrentUser();
  const liveUiMessages = useLiveUiMessages();

  const loadMessages = useCallback(async (id: string) => {
    const list = await fetchChatMessages(id);
    setMessages(list);
    const latestGift = [...list].reverse().find((msg) => msg.message_type === 'gift');
    if (latestGift && latestGift.id > lastGiftIdRef.current) {
      if (lastGiftIdRef.current > 0) {
        showOneliveToast(latestGift.message, 'success');
      }
      lastGiftIdRef.current = latestGift.id;
    }
  }, []);

  useEffect(() => {
    if (!resolvedRoomId) {
      setPhase('error');
      setErrorText('채팅방 정보를 찾을 수 없습니다.');
      return;
    }

    let mounted = true;
    setPhase('loading');
    setEntered(false);
    enteredRef.current = false;
    setRoom(null);
    setMessages([]);
    lastScrolledMsgIdRef.current = 0;

    (async () => {
      try {
        const member = (await fetchCurrentMemberProfile()) ?? getCurrentUser();
        if (!mounted) return;
        setMyProfile(member);

        if (!member?.uuid) {
          setPhase('error');
          setErrorText('로그인이 필요합니다.');
          handleLoginRequired(liveUiMessages, `/chat/room/${resolvedRoomId}`);
          return;
        }

        const detail = await fetchChatRoom(resolvedRoomId);
        if (!mounted) return;

        if (!detail) {
          setPhase('error');
          setErrorText('채팅방을 찾을 수 없습니다.');
          return;
        }

        setRoom(detail);

        const { room: enteredRoom, error } = await enterChatRoom(resolvedRoomId);
        if (!mounted) return;

        if (!enteredRoom) {
          setPhase('error');
          const msg = error || '채팅방에 입장할 수 없습니다.';
          showOneliveAlert(msg.includes('만원') ? msg : (error || '채팅방이 만원입니다.'), () => navigate('/chat'));
          return;
        }

        setRoom(enteredRoom);
        setEntered(true);
        enteredRef.current = true;
        await loadMessages(resolvedRoomId);
        setPhase('ready');
      } catch {
        if (mounted) {
          setPhase('error');
          setErrorText('채팅방을 불러오지 못했습니다.');
        }
      }
    })();

    const timer = window.setInterval(() => {
      if (enteredRef.current) {
        void loadMessages(resolvedRoomId);
      }
    }, 5000);

    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [loadMessages, resolvedRoomId]);

  useEffect(() => {
    if (!listRef.current || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.id === lastScrolledMsgIdRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
    lastScrolledMsgIdRef.current = lastMsg.id;
  }, [messages]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!resolvedRoomId || !input.trim() || sending || !entered) return;

    setSending(true);
    try {
      const sent = await sendChatMessage(resolvedRoomId, input);
      if (!sent) {
        showOneliveToast('메시지 전송에 실패했습니다.', 'warning');
        return;
      }
      setInput('');
      await loadMessages(resolvedRoomId);
    } finally {
      setSending(false);
    }
  };

  const onPickImage = () => {
    if (!entered || uploadingImage) return;
    fileRef.current?.click();
  };

  const onImageSelected = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !resolvedRoomId || !entered) return;

    setUploadingImage(true);
    try {
      const sent = await sendChatImage(resolvedRoomId, file);
      if (!sent) {
        showOneliveToast('이미지 전송에 실패했습니다.', 'warning');
        return;
      }
      await loadMessages(resolvedRoomId);
    } finally {
      setUploadingImage(false);
    }
  };

  const renderMessageBody = (msg: ChatMessage) => {
    if (msg.message_type === 'gift') {
      return <div className="onelive-chat-msg-gift">{msg.message}</div>;
    }
    if (msg.message_type === 'image' && msg.image_url) {
      return (
        <div className="onelive-chat-msg-bubble">
          <img src={msg.image_url} alt="" />
        </div>
      );
    }
    return <div className="onelive-chat-msg-bubble">{msg.message}</div>;
  };

  const cap = room ? roomCapacity(room) : { current: 0, max: 2 };
  const hostAvatarUrl = room ? resolveChatRoomThumbOptional(room) : null;
  const myAvatarUrl = resolveMemberAvatarUrl(myProfile);
  const myNickname = myProfile?.nickname || user?.nickname || user?.name || '나';
  const hostName = room?.host_nickname ?? '';

  const goBackToList = () => {
    if (resolvedRoomId && enteredRef.current) {
      void leaveChatRoom(resolvedRoomId);
    }
    navigate('/chat');
  };

  const isHostMessage = (msg: ChatMessage) => Boolean(hostName) && msg.nickname === hostName;
  const isMyMessage = (msg: ChatMessage) => !isHostMessage(msg) && (msg.nickname === myNickname || msg.nickname === user?.name);

  const renderChatRow = (msg: ChatMessage) => {
    const hostSide = isHostMessage(msg);
    const mine = isMyMessage(msg);
    const avatarUrl = hostSide ? hostAvatarUrl : myAvatarUrl;
    const rowClass = hostSide ? 'onelive-chat-msg-row--host' : mine ? 'onelive-chat-msg-row--mine' : 'onelive-chat-msg-row--guest';

    return (
      <div key={msg.id} className={`onelive-chat-msg-row ${rowClass}`}>
        <ChatAvatar src={avatarUrl} className="onelive-chat-msg-avatar" />
        <div className="onelive-chat-msg-content">
          <div className="onelive-chat-msg-top">
            <strong className="onelive-chat-msg-name">{msg.nickname}</strong>
          </div>
          {renderMessageBody(msg)}
          <time className="onelive-chat-msg-time">{formatMessageTime(msg.sent_at)}</time>
        </div>
      </div>
    );
  };

  return (
    <OnelivePageLayout pageClassName="onelive-mobile-chat-room-page">
      <div className="onelive-ref-sub-title"><span>채팅방</span></div>

          {phase === 'loading' && (
            <div className="onelive-chat-phone onelive-chat-phone--loading">
              <p className="onelive-loading ref-chat-loading">채팅방 입장 중...</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="ref-chat-empty onelive-chat-phone-error">
              <p>{errorText || '채팅방을 찾을 수 없습니다.'}</p>
              <button type="button" className="ref-chat-login-btn" onClick={goBackToList}>
                목록으로
              </button>
            </div>
          )}

          {phase === 'ready' && room && (
            <div className="onelive-chat-phone">
              <header className="onelive-chat-phone-head">
                <button type="button" className="onelive-chat-phone-back" onClick={goBackToList}>
                  ←
                </button>
                <h3>
                  <ChatAvatar src={hostAvatarUrl} className="onelive-chat-phone-head-avatar" />
                  <span>{room.title || room.host_nickname}</span>
                </h3>
                <div className="onelive-chat-phone-capacity">{cap.current}/{cap.max}</div>
              </header>

              <div className="onelive-chat-phone-body" ref={listRef}>
                <div className="onelive-chat-live-chat">
                  {messages.length === 0 ? (
                    <p className="onelive-chat-live-empty">대화를 시작해 보세요.</p>
                  ) : (
                    messages.map((msg) => {
                      if (msg.message_type === 'gift') {
                        return (
                          <div key={msg.id} className="onelive-chat-msg onelive-chat-msg--gift">
                            {renderMessageBody(msg)}
                            <time className="onelive-chat-msg-time onelive-chat-msg-time--center">{formatMessageTime(msg.sent_at)}</time>
                          </div>
                        );
                      }
                      return renderChatRow(msg);
                    })
                  )}
                </div>
              </div>

              <form className="onelive-chat-phone-footer" onSubmit={onSubmit}>
                <button type="button" className="onelive-chat-image-btn" onClick={onPickImage} disabled={uploadingImage} aria-label="이미지 전송">
                  <ChatCameraIcon />
                </button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onImageSelected} />
                <input
                  type="text"
                  className="onelive-chat-input"
                  value={input}
                  onChange={(ev) => setInput(ev.target.value)}
                  placeholder="메시지를 입력하세요"
                  maxLength={500}
                />
                <button type="submit" className="onelive-chat-send-btn" disabled={sending || !input.trim()}>
                  전송
                </button>
              </form>
            </div>
          )}
    </OnelivePageLayout>
  );
}
